import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

const ThemeModeContext = createContext();

const STORAGE_KEY = 'internull-theme-mode';
const LEGACY_STORAGE_KEY = 'blackbox-theme-mode';

export const useThemeMode = () => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider');
  }
  return context;
};

export const ThemeModeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || localStorage.getItem(LEGACY_STORAGE_KEY) || 'dark';
    } catch {
      return 'dark';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {}

    // Sync Tailwind dark class on <html>
    const html = document.documentElement;
    if (mode === 'dark') {
      html.classList.add('dark');
    } else {
      html.classList.remove('dark');
    }

    // Sync body background
    document.body.style.background = mode === 'dark' ? '#050505' : '#fafafa';

    // Sync meta theme-color
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute('content', mode === 'dark' ? '#000000' : '#fafafa');
    }
  }, [mode]);

  const toggleMode = () => setMode(prev => prev === 'dark' ? 'light' : 'dark');
  const isDark = mode === 'dark';

  const value = useMemo(() => ({ mode, toggleMode, isDark }), [mode, isDark]);

  return (
    <ThemeModeContext.Provider value={value}>
      {children}
    </ThemeModeContext.Provider>
  );
};
